import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  LoadingOverlay,
  Modal,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import type {
  Payment,
  PaymentRequirement,
  PaymentRequirementTarget,
} from '@pta/types';
import { fetchRequirement } from '../api/payment-requirements';
import { ApiError } from '../api/client';
import { useMoneyFormatter } from '../util/money';

/**
 * Student debts modal — egy diák összes nyitott tartozása a csoporton belül.
 *
 * Pénzformázás: a csoport `GroupLocale`-jából (useMoneyFormatter).
 */
interface StudentDebtsModalProps {
  opened: boolean;
  onClose: () => void;
  studentId: number;
  studentName: string;
  requirements: PaymentRequirement[];
  payments: Payment[];
}

interface DebtRow {
  requirement: PaymentRequirement;
  target: PaymentRequirementTarget;
  paid: number;
  remaining: number;
}

export function StudentDebtsModal({
  opened,
  onClose,
  studentId,
  studentName,
  requirements,
  payments,
}: StudentDebtsModalProps) {
  const { money } = useMoneyFormatter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<DebtRow[]>([]);
  const [waivedRows, setWaivedRows] = useState<
    Array<{ requirement: PaymentRequirement; target: PaymentRequirementTarget }>
  >([]);

  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);
    setWaivedRows([]);

    void (async () => {
      try {
        const debts: DebtRow[] = [];
        const waived: typeof waivedRows = [];
        for (const r of requirements) {
          const full = await fetchRequirement(r.id, { includeTargets: true });
          const target = (full.targets ?? []).find((t) => t.studentId === studentId);
          if (!target) continue;
          if (target.waived) {
            waived.push({ requirement: r, target });
            continue;
          }
          const paid = payments
            .filter((p) => p.targetId === target.id)
            .reduce((acc, p) => acc + Number(p.amount), 0);
          const remaining = Number(target.expectedAmount) - paid;
          if (remaining > 0) {
            debts.push({ requirement: r, target, paid, remaining });
          }
        }
        if (!cancelled) {
          setRows(debts);
          setWaivedRows(waived);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [opened, studentId, requirements, payments]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`${studentName} — tartozások`}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      size="lg"
    >
      <Stack pos="relative" gap="md">
        <LoadingOverlay visible={loading} />

        {error && <Alert color="red">{error}</Alert>}

        {!loading && rows.length === 0 && waivedRows.length === 0 && (
          <Alert color="green" variant="light" icon={<IconInfoCircle size={18} />}>
            Nincs nyitott tartozás.
          </Alert>
        )}

        {rows.length > 0 && (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Előírás</Table.Th>
                <Table.Th ta="right">Várt</Table.Th>
                <Table.Th ta="right">Fizetve</Table.Th>
                <Table.Th ta="right">Hátralék</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.target.id}>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm" fw={500}>
                        {r.requirement.title}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {r.requirement.purpose}
                        {r.requirement.dueDate ? ` • határidő: ${r.requirement.dueDate}` : ''}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td ta="right">{money(r.target.expectedAmount)}</Table.Td>
                  <Table.Td ta="right">{money(r.paid)}</Table.Td>
                  <Table.Td ta="right">
                    <Text fw={500} c="red">
                      {money(r.remaining)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
              <Table.Tr>
                <Table.Td colSpan={3} style={{ textAlign: 'right' }}>
                  <Text fw={500}>Összes nyitott tartozás:</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text fw={700} c="red">
                    {money(rows.reduce((acc, r) => acc + r.remaining, 0))}
                  </Text>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        )}

        {waivedRows.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={500} c="dimmed">
              Mentesített előírások
            </Text>
            <Table>
              <Table.Tbody>
                {waivedRows.map((w) => (
                  <Table.Tr key={w.target.id}>
                    <Table.Td>
                      <Text size="sm">{w.requirement.title}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color="orange" variant="light" size="sm">
                        Mentesítve
                      </Badge>
                      {w.target.waiveReason && (
                        <Text size="xs" c="dimmed">{w.target.waiveReason}</Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
